"use client"

import React, {useCallback, useMemo, useState} from "react";
import {useForm} from "react-hook-form";
import {ReactWrapperController} from "@root/server/client/esweb/src/lib/stimulus/ReactWrapperController.ts";
import {Context} from "@hotwired/stimulus";
import {FormServerErrors} from "@root/server/client/esweb/src/pages/web/shared/form-server-errors.tsx";
import {useFormSubmission} from "@root/server/client/esweb/src/lib/hooks/useFormSubmission.ts";
import {RobustUpload, type UploadCompletePayload, type UploadErrorPayload} from "@root/server/client/esweb/src/lib/components/upload/RobustUpload.stimulus.tsx";
import {UploadGallery} from "@root/server/client/esweb/src/lib/components/gallery/UploadGallery.tsx";
import {cn} from "@root/server/client/esweb/lib/utils.ts";
import {Button} from "@root/server/client/esweb/components/ui/button.tsx";
import {ChevronRight} from "lucide-react";
import {UploadedFile} from "@root/server/client/esweb/src/lib/components/gallery/galleryTypes.ts";
import {fetchApi} from "@root/server/client/esweb/src/lib/fetchApi.ts";

export const name = 'pwm--service_gallery_form';

export default class extends ReactWrapperController {
    constructor(context: Context) {
        super(context, (props) => <MovingGalleryForm {...props}/>);
    }
}

import Debug from 'debug';
const debug = Debug(`app:${name}`);

export type ServiceGalleryFormValues = Record<string, never>;

interface MovingGalleryFormProps {
    initialData?: Partial<ServiceGalleryFormValues>;
    submitUrl: string;
    uploadAction: string;
    initialFiles?: UploadedFile[];
    deleteBaseUrl?: string;
    qrDataUrl?: string;
    signedChannel?: string;
}

export function MovingGalleryForm({
    initialData,
    submitUrl,
    uploadAction,
    initialFiles = [],
    deleteBaseUrl = "/web/upload",
    qrDataUrl,
    signedChannel,
}: MovingGalleryFormProps) {
    const {handleSubmit} = useForm<ServiceGalleryFormValues>({
        mode: "onTouched",
        reValidateMode: "onChange",
        defaultValues: {
            ...(initialData ?? {}),
        },
    });

    const {execute, isSubmitting, serverErrors} = useFormSubmission<ServiceGalleryFormValues>({submitUrl});

    const [files, setFiles] = useState<UploadedFile[]>(initialFiles);
    const [removingIds, setRemovingIds] = useState<Set<string>>(new Set());
    const [isUploading, setIsUploading] = useState(false);

    const actionUrl = useMemo(() => new URL(uploadAction, window.location.origin), [uploadAction]);
    const actionQuery = actionUrl.search;
    const deleteBase = useMemo(() => deleteBaseUrl.replace(/\/$/, ""), [deleteBaseUrl]);

    const parseUploadResponse = useCallback((response: unknown): UploadedFile | null => {
        debug('parseUploadResponse', {response});

        if (!response) {
            return null;
        }

        if (typeof response === "string") {
            try {
                const parsed = JSON.parse(response);
                return parsed?.file ?? null;
            } catch {
                return null;
            }
        }

        if (typeof response === "object") {
            const maybe = response as {file?: UploadedFile};
            return maybe.file ?? null;
        }

        return null;
    }, []);

    const handleUploadComplete = useCallback((payload: UploadCompletePayload) => {
        const parsedFile = parseUploadResponse(payload.response);
        debug('handleUploadCompelete', {payload, parsedFile});

        if (parsedFile) {
            setFiles((prev) => {
                const exists = prev.some((item) => item.id === parsedFile.id);
                return exists ? prev : [parsedFile, ...prev];
            });
        }
    }, [parseUploadResponse]);

    const handleUploadError = useCallback((payload: UploadErrorPayload) => {
        console.error("Upload failed", payload);
    }, []);

    const handleRemove = useCallback(async (file: UploadedFile) => {
        setRemovingIds((prev) => {
            const next = new Set(prev);
            next.add(file.id);
            return next;
        });

        try {
            const deleteUrl = `${deleteBase}/${file.id}${actionQuery}`;
            await fetchApi(deleteUrl, {method: "DELETE"});

            setFiles((prev) => prev.filter((item) => item.id !== file.id));
        } catch (error) {
            console.error("Failed to delete file", error);
        } finally {
            setRemovingIds((prev) => {
                const next = new Set(prev);
                next.delete(file.id);
                return next;
            });
        }
    }, [actionQuery, deleteBase]);

    const buttonLabel = files.length > 0 ? "Pokračovat" : "Pokračovat bez fotek";
    const isSubmitDisabled = isSubmitting || isUploading;

    return (
        <form onSubmit={handleSubmit(execute)} className="space-y-6">
            <FormServerErrors errors={serverErrors} />

            <div className="space-y-8">
                <div className={cn("flex flex-col gap-6", qrDataUrl && "md:flex-row md:items-start")}>
                    <div className="flex-1">
                        <RobustUpload
                            {...{signedChannel}}
                            action={uploadAction}
                            accept="image/*"
                            responseType="json"
                            headers={{"x-csrf-token": window.clientConfig.CSRF_TOKEN}}
                            onComplete={handleUploadComplete}
                            onError={handleUploadError}
                            onUploadingChange={(value) => setIsUploading(value)}
                        />
                    </div>

                    {qrDataUrl && (
                        <div className="hidden w-full items-center justify-center md:flex md:w-auto md:shrink-0">
                            <div className="flex w-full max-w-xs flex-col items-center gap-3 rounded-lg border bg-card p-4 text-center">
                                <img
                                    src={qrDataUrl}
                                    alt="QR kód pro mobilní nahrávání"
                                    className="h-44 w-44 rounded-md border bg-white object-contain"
                                />
                                <div className="text-sm text-muted-foreground">
                                    Naskenujte QR kód mobilem pro rychlé focení a nahrání fotek.
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <Button type="submit" size="lg" className="w-full" disabled={isSubmitDisabled}>
                    {isSubmitting ? "Ukládám..." : buttonLabel}
                    <ChevronRight />
                </Button>

                <UploadGallery
                    files={files}
                    removingIds={removingIds}
                    onRemove={handleRemove}
                />
            </div>
        </form>
    );
}
